import { useEffect, useState } from "react";
import {
  CalendarClock,
  CheckCircle2,
  Pencil,
  RotateCcw,
  X,
} from "lucide-react";

import { useCurrency } from "@/context/CurrencyContext";
import { api, apiError } from "@/lib/api";
import {
  boliviaDateInputWithOffset,
  boliviaTodayInput,
} from "@/lib/dateTime";

const expiryDate = () => boliviaDateInputWithOffset(7);

export default function LotOperationsTable({ lots, user, onChanged }) {
  const { formatAmount } = useCurrency();

  const [clients, setClients] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [operation, setOperation] = useState(null);
  const [clientId, setClientId] = useState("");
  const [amount, setAmount] = useState("");
  const [expiresAt, setExpiresAt] = useState(expiryDate());
  const [notes, setNotes] = useState("");
  const [message, setMessage] = useState("");
  const [editingLot, setEditingLot] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/clients").then((response) => setClients(response.data));
    api
      .get("/settings/lot-statuses")
      .then((response) => setStatuses(response.data));
  }, []);

  const statusByName = new Map(
    statuses.map((status) => [status.name, status])
  );

  const canOperate =
    user.role === "vendedor" || user.role === "administrador";

  const openOperation = (type, lot) => {
    setOperation({ type, lot });
    setClientId(lot.reserva?.client_id || "");
    setAmount(lot.reserva?.monto_reserva || "");
    setExpiresAt(
      lot.reserva?.fecha_vencimiento || expiryDate()
    );
    setNotes(lot.reserva?.observaciones || "");
    setMessage("");
  };

  const filteredLots = lots.filter((lot) => {
    const term = search.trim().toLowerCase();

    if (!term) return true;

    return [
      lot.manzano,
      lot.lote,
      lot.estado,
      lot.clasificacion,
      lot.observaciones,
      lot.fase,
    ]
      .filter(
        (value) =>
          value !== null && value !== undefined
      )
      .some((value) =>
        String(value).toLowerCase().includes(term)
      );
  });

  const submit = async (event) => {
    event.preventDefault();

    if (!operation) return;

    try {
      if (operation.type === "reserve") {
        await api.post(
          `/lots/${operation.lot.id}/reservation`,
          {
            client_id: clientId,
            monto_reserva: Number(amount),
            fecha_vencimiento: expiresAt,
            observaciones: notes,
          }
        );
      }

      if (operation.type === "sell") {
        await api.post(
          `/lots/${operation.lot.id}/sale`,
          {
            client_id: clientId || null,
          }
        );
      }

      if (operation.type === "release") {
        await api.post(
          `/lots/${operation.lot.id}/available`
        );
      }

      setOperation(null);
      onChanged();
    } catch (error) {
      setMessage(apiError(error));
    }
  };

  const assignStatus = async (lot, statusId) => {
    try {
      await api.put(`/lots/${lot.id}/status`, {
        status_id: statusId,
      });

      onChanged();
    } catch (error) {
      setMessage(apiError(error));
    }
  };

  const saveLot = async (event) => {
    event.preventDefault();

    try {
      await api.put(`/lots/${editingLot.id}`, {
        superficie: Number(editingLot.superficie),
        precio_m2: Number(editingLot.precio_m2),
        fase: editingLot.fase,
        observaciones: editingLot.observaciones,
      });

      setEditingLot(null);
      onChanged();
    } catch (error) {
      setMessage(apiError(error));
    }
  };

  return (
    <>
      {/* BUSCADOR */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 18,
          flexWrap: "wrap",
        }}
      >
        <input
          type="search"
          value={search}
          onChange={(event) =>
            setSearch(event.target.value)
          }
          placeholder="Buscar manzano, lote, estado, fase u observación..."
          aria-label="Buscar manzano, lote, estado, fase u observación..."
          style={{
            flex: "1 1 320px",
            maxWidth: 520,
            boxSizing: "border-box",
            padding: "12px 16px",
            border: "1px solid #d8e0e8",
            borderRadius: 8,
            fontSize: 14,
            outline: "none",
          }}
        />

        {search && (
          <button
            type="button"
            className="outline-button"
            onClick={() => setSearch("")}
          >
            Limpiar
          </button>
        )}

        <span
          style={{
            color: "#68778a",
            fontSize: 14,
            whiteSpace: "nowrap",
          }}
        >
          {filteredLots.length.toLocaleString("es-BO")} de{" "}
          {lots.length.toLocaleString("es-BO")} lotes
        </span>
      </div>

      {/* TABLA */}
      <div className="data-table-wrap short-table">
        <table
          data-testid="lot-operations-table"
          style={{
            width: "100%",
            minWidth: "1350px",
            borderCollapse: "collapse",
          }}
        >
          <thead>
            <tr>
              <th>Manzano</th>
              <th>Lote</th>
              <th>Superficie (m²)</th>
              <th>Precio/m²</th>
              <th>Precio total</th>
              <th>Fase</th>
              <th>Observaciones</th>
              <th>Estado</th>
              <th>Operación</th>
            </tr>
          </thead>

          <tbody>
            {filteredLots.map((lot) => {
              const status = statusByName.get(
                lot.estado
              );

              const canReserve =
                canOperate &&
                status?.vendor_can_sell &&
                ["disponible", "informativo"].includes(
                  status.category
                );

              const canSell =
                canOperate &&
                status?.category === "reservado" &&
                status.vendor_can_sell;

              const canAdminSell =
                user.role === "administrador" &&
                ["disponible", "informativo"].includes(
                  status?.category
                );

              return (
                <tr
                  data-testid={`lot-operation-row-${lot.id}`}
                  key={lot.id}
                >
                  {/* MANZANO */}
                  <td>
                    {lot.manzano}
                  </td>

                  {/* LOTE */}
                  <td>
                    {lot.lote}
                  </td>

                  {/* SUPERFICIE - NO ES DINERO */}
                  <td
                    style={{
                      whiteSpace: "nowrap",
                    }}
                  >
                    {Number(
                      lot.superficie || 0
                    ).toLocaleString("es-BO", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{" "}
                    m²
                  </td>

                  {/* PRECIO POR M2 */}
                  <td
                    style={{
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatAmount(lot.precio_m2)}
                  </td>

                  {/* PRECIO TOTAL */}
                  <td
                    style={{
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatAmount(
                      lot.precio_total
                    )}
                  </td>

                  {/* FASE */}
                  <td>
                    {lot.fase || "—"}
                  </td>

                  {/* OBSERVACIONES */}
                  <td
                    title={
                      lot.observaciones || ""
                    }
                    style={{
                      minWidth: 260,
                      maxWidth: 360,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {lot.observaciones || "—"}
                  </td>

                  {/* ESTADO */}
                  <td>
                    {user.role ===
                    "administrador" ? (
                      <select
                        data-testid={`lot-status-select-${lot.id}`}
                        onChange={(event) =>
                          assignStatus(
                            lot,
                            event.target.value
                          )
                        }
                        value={
                          status?.id || ""
                        }
                      >
                        {statuses.map(
                          (item) => (
                            <option
                              key={item.id}
                              value={item.id}
                            >
                              {item.name}
                            </option>
                          )
                        )}
                      </select>
                    ) : (
                      <span
                        className="status-tag"
                        style={{
                          backgroundColor:
                            status?.color,
                          color: "#173f5e",
                        }}
                      >
                        {lot.estado}
                      </span>
                    )}
                  </td>

                  {/* OPERACIONES */}
                  <td>
                    <div
                      className="lot-action-buttons"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexWrap: "wrap",
                      }}
                    >
                      {user.role ===
                        "administrador" && (
                        <button
                          aria-label="Editar lote"
                          className="icon-button"
                          data-testid={`edit-lot-${lot.id}`}
                          onClick={() =>
                            setEditingLot({
                              ...lot,
                            })
                          }
                          type="button"
                        >
                          <Pencil size={16} />
                        </button>
                      )}

                      {canReserve && (
                        <button
                          className="outline-button"
                          data-testid={`reserve-lot-${lot.id}`}
                          onClick={() =>
                            openOperation(
                              "reserve",
                              lot
                            )
                          }
                          type="button"
                        >
                          <CalendarClock
                            size={15}
                          />{" "}
                          Reservar
                        </button>
                      )}

                      {(canAdminSell ||
                        canSell) && (
                        <button
                          className="primary-button"
                          data-testid={`sell-lot-${lot.id}`}
                          onClick={() =>
                            openOperation(
                              "sell",
                              lot
                            )
                          }
                          type="button"
                        >
                          <CheckCircle2
                            size={15}
                          />{" "}
                          {canSell
                            ? "Concretar venta"
                            : "Vender"}
                        </button>
                      )}

                      {user.role ===
                        "administrador" &&
                        status?.category !==
                          "disponible" && (
                          <button
                            className="icon-button release-lot-button"
                            data-testid={`release-lot-${lot.id}`}
                            onClick={() =>
                              openOperation(
                                "release",
                                lot
                              )
                            }
                            title="Volver a disponible"
                            type="button"
                          >
                            <RotateCcw
                              size={16}
                            />
                          </button>
                        )}

                      {!canOperate && (
                        <span className="muted-cell">
                          Sin permiso operativo
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* MODAL RESERVA / VENTA / LIBERAR */}
      {operation && (
        <div
          className="lot-operation-modal"
          data-testid="lot-operation-modal"
        >
          <form
            className="lot-operation-form"
            data-testid="lot-operation-form"
            onSubmit={submit}
          >
            <button
              aria-label="Cerrar operación"
              className="icon-button modal-close"
              data-testid="close-lot-operation"
              onClick={() =>
                setOperation(null)
              }
              type="button"
            >
              <X size={18} />
            </button>

            <span className="eyebrow">
              {operation.type === "reserve"
                ? "RESERVA"
                : operation.type === "sell"
                ? "VENTA"
                : "LIBERAR LOTE"}
            </span>

            <h2>
              Manzano{" "}
              {operation.lot.manzano} · Lote{" "}
              {operation.lot.lote}
            </h2>

            {operation.type !==
              "release" && (
              <>
                <label htmlFor="operation-client">
                  Cliente
                </label>

                <select
                  data-testid="operation-client-select"
                  id="operation-client"
                  onChange={(event) =>
                    setClientId(
                      event.target.value
                    )
                  }
                  required
                  value={clientId}
                >
                  <option value="">
                    Selecciona un cliente
                  </option>

                  {clients.map(
                    (client) => (
                      <option
                        key={client.id}
                        value={client.id}
                      >
                        {client.nombre} ·{" "}
                        {client.ci}
                      </option>
                    )
                  )}
                </select>
              </>
            )}

            {operation.type ===
              "reserve" && (
              <>
                <label htmlFor="reservation-amount">
                  Monto de reserva
                </label>

                <input
                  data-testid="reservation-amount-input"
                  id="reservation-amount"
                  min="0.01"
                  onChange={(event) =>
                    setAmount(
                      event.target.value
                    )
                  }
                  required
                  step="0.01"
                  type="number"
                  value={amount}
                />

                <label htmlFor="reservation-expiry">
                  Vence el
                </label>

                <input
                  data-testid="reservation-expiry-input"
                  id="reservation-expiry"
                  min={boliviaTodayInput()}
                  onChange={(event) =>
                    setExpiresAt(
                      event.target.value
                    )
                  }
                  required
                  type="date"
                  value={expiresAt}
                />

                <label htmlFor="reservation-notes">
                  Observaciones
                </label>

                <textarea
                  data-testid="reservation-notes-input"
                  id="reservation-notes"
                  onChange={(event) =>
                    setNotes(
                      event.target.value
                    )
                  }
                  value={notes}
                />
              </>
            )}

            {operation.type === "sell" && (
              <p
                className="operation-note"
                data-testid="sale-quote-note"
              >
                Se utilizará automáticamente
                la última cotización de este
                cliente para el lote.
              </p>
            )}

            {operation.type ===
              "release" && (
              <p className="operation-note">
                El lote volverá a estar
                disponible. Esta acción queda
                registrada.
              </p>
            )}

            {message && (
              <p
                className="form-error"
                data-testid="lot-operation-error"
              >
                {message}
              </p>
            )}

            <button
              className="primary-button"
              data-testid="confirm-lot-operation"
              type="submit"
            >
              {operation.type === "reserve"
                ? "Confirmar reserva"
                : operation.type === "sell"
                ? "Confirmar venta"
                : "Volver a disponible"}
            </button>
          </form>
        </div>
      )}

      {/* MODAL EDITAR LOTE */}
      {editingLot && (
        <div
          className="user-edit-modal"
          data-testid="lot-edit-modal"
        >
          <form
            className="user-edit-form"
            data-testid="lot-edit-form"
            onSubmit={saveLot}
          >
            <button
              aria-label="Cerrar edición de lote"
              className="icon-button modal-close"
              data-testid="close-lot-edit"
              onClick={() =>
                setEditingLot(null)
              }
              type="button"
            >
              <X size={18} />
            </button>

            <span className="eyebrow">
              EDITAR LOTE
            </span>

            <h2>
              Manzano{" "}
              {editingLot.manzano} · Lote{" "}
              {editingLot.lote}
            </h2>

            <p className="operation-note">
              Manzano y lote se mantienen
              fijos para conservar la
              vinculación geográfica con el
              plano.
            </p>

            <label htmlFor="edit-lot-surface">
              Superficie (m²)
            </label>

            <input
              data-testid="edit-lot-surface-input"
              id="edit-lot-surface"
              min="0.01"
              onChange={(event) =>
                setEditingLot({
                  ...editingLot,
                  superficie:
                    event.target.value,
                })
              }
              required
              step="0.01"
              type="number"
              value={editingLot.superficie}
            />

            <label htmlFor="edit-lot-price">
              Precio por m²
            </label>

            <input
              data-testid="edit-lot-price-input"
              id="edit-lot-price"
              min="0.01"
              onChange={(event) =>
                setEditingLot({
                  ...editingLot,
                  precio_m2:
                    event.target.value,
                })
              }
              required
              step="0.01"
              type="number"
              value={editingLot.precio_m2}
            />

            <label htmlFor="edit-lot-phase">
              Fase
            </label>

            <input
              data-testid="edit-lot-phase-input"
              id="edit-lot-phase"
              onChange={(event) =>
                setEditingLot({
                  ...editingLot,
                  fase: event.target.value,
                })
              }
              value={editingLot.fase || ""}
            />

            <label htmlFor="edit-lot-notes">
              Observaciones
            </label>

            <textarea
              data-testid="edit-lot-notes-input"
              id="edit-lot-notes"
              onChange={(event) =>
                setEditingLot({
                  ...editingLot,
                  observaciones:
                    event.target.value,
                })
              }
              value={
                editingLot.observaciones || ""
              }
            />

            <button
              className="primary-button"
              data-testid="save-lot-edit-button"
              type="submit"
            >
              Guardar lote
            </button>
          </form>
        </div>
      )}
    </>
  );
}